import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import API from '../../services/api'

// ─── Helpers ───────────────────────────────────────────────────────────────────

const buildParams = ({ estado = 'general', fecha, ruta, page = 1, limit = 25 }) => {
  const params = { estado, page, limit };
  if (fecha) params.fecha = fecha; // opcional: si no hi ha fecha, l'almacén torna tots els dies
  if (ruta) params.ruta = ruta;
  return params;
};

// ─── Thunks ───────────────────────────────────────────────────────────────────

export const fetchRepartos = createAsyncThunk(
  'repartos/fetch',
  async (args = {}, { rejectWithValue }) => {
    const params = buildParams(args);
    try {
      const response = await API.get('/repartos', { params });
      return { estado: params.estado, ...response.data };
    } catch (err) {
      return rejectWithValue({ estado: params.estado, error: err.response?.data || err.message });
    }
  }
);

export const fetchRefreshRepartos = createAsyncThunk(
  'repartos/refresh',
  async (args = {}, { rejectWithValue }) => {
    const params = buildParams(args);
    try {
      const response = await API.get('/repartos', { params });
      return { estado: params.estado, ...response.data };
    } catch (err) {
      return rejectWithValue({ estado: params.estado, error: err.response?.data || err.message });
    }
  }
);

export const confirmarEntrega = createAsyncThunk(
  'repartos/confirmarEntrega',
  async ({ id, foto }, { rejectWithValue }) => {
    try {
      const response = await API.post(`/repartos/${id}/confirmar-entrega`, { foto });
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const prorrogarEntrega = createAsyncThunk(
  'repartos/prorrogarEntrega',
  async ({ id, nuevaFecha }, { rejectWithValue }) => {
    try {
      const response = await API.post(`/repartos/${id}/prorrogar`, { nueva_fecha: nuevaFecha });
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const rechazarEntrega = createAsyncThunk(
  'repartos/rechazarEntrega',
  async ({ id, motivo }, { rejectWithValue }) => {
    try {
      const response = await API.post(`/repartos/${id}/rechazar`, { motivo });
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

// ─── Estado inicial ───────────────────────────────────────────────────────────

const emptyTab = () => ({
  data: [],
  pagination: { page: 1, limit: 25, total: 0, totalPages: 1 },
  status: 'idle',
  error: null,
});

const initialState = {
  kpis: {
    en_reparto: 0,
    entregados_hoy: 0,
    preparados_manana: 0,
    aplazados: 0,
    saved_cache_at: null,
  },
  tabs: {
    general: emptyTab(),
    entregados: emptyTab(),
    rechazados: emptyTab(),
    todos: emptyTab(),
  },
  refreshing: false,
};

// ─── Slice ────────────────────────────────────────────────────────────────────

const applyFulfilled = (state, action) => {
  const { estado, kpis, data, pagination } = action.payload;
  if (!state.tabs[estado]) state.tabs[estado] = emptyTab();
  const tab = state.tabs[estado];
  tab.status = 'succeeded';
  tab.error = null;
  tab.data = data ?? [];
  tab.pagination = pagination ?? tab.pagination;
  state.kpis = kpis ?? state.kpis;
};

const repartosSlice = createSlice({
  name: 'repartos',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchRepartos.pending, (state, action) => {
        const estado = action.meta.arg?.estado ?? 'general';
        if (!state.tabs[estado]) state.tabs[estado] = emptyTab();
        state.tabs[estado].status = 'loading';
        state.tabs[estado].error = null;
      })
      .addCase(fetchRepartos.fulfilled, applyFulfilled)
      .addCase(fetchRepartos.rejected, (state, action) => {
        const estado = action.payload?.estado ?? 'general';
        if (!state.tabs[estado]) state.tabs[estado] = emptyTab();
        state.tabs[estado].status = 'failed';
        state.tabs[estado].error = action.payload?.error;
      });

    builder
      .addCase(fetchRefreshRepartos.pending, (state) => { state.refreshing = true; })
      .addCase(fetchRefreshRepartos.fulfilled, (state, action) => {
        state.refreshing = false;
        applyFulfilled(state, action);
      })
      .addCase(fetchRefreshRepartos.rejected, (state) => { state.refreshing = false; });

    // Nota: com que ara les dades venen paginades per pestanya, aquestes actualitzacions
    // optimistes només toquen la pestanya "general" (d'on surt l'acció). La pestanya
    // "Entregados"/"Rechazados" es refrescarà sola quan l'usuari hi entri o premi "Actualizar".
    builder.addCase(confirmarEntrega.fulfilled, (state, action) => {
      const { rows } = action.payload;
      const pedidoId = rows?.[0]?.pedidoId;
      const general = state.tabs.general;
      const hadRows = general.data.some(r => r.pedidoId === pedidoId);
      general.data = general.data.filter(r => r.pedidoId !== pedidoId);
      if (hadRows) {
        general.pagination.total = Math.max(0, general.pagination.total - 1);
      }
      state.kpis.entregados_hoy = (state.kpis.entregados_hoy || 0) + 1;
      state.kpis.en_reparto = Math.max(0, (state.kpis.en_reparto || 0) - 1);
    });

    builder.addCase(prorrogarEntrega.fulfilled, (state, action) => {
      const { rows } = action.payload;
      const pedidoId = rows?.[0]?.pedidoId;
      const general = state.tabs.general;
      general.data = general.data.flatMap(r => (r.pedidoId === pedidoId ? rows : [r]));
      state.kpis.aplazados = (state.kpis.aplazados || 0) + 1;
    });

    builder.addCase(rechazarEntrega.fulfilled, (state, action) => {
      const { rows } = action.payload;
      const pedidoId = rows?.[0]?.pedidoId;
      const general = state.tabs.general;
      const hadRows = general.data.some(r => r.pedidoId === pedidoId);
      general.data = general.data.filter(r => r.pedidoId !== pedidoId);
      if (hadRows) {
        general.pagination.total = Math.max(0, general.pagination.total - 1);
      }
      state.kpis.en_reparto = Math.max(0, (state.kpis.en_reparto || 0) - 1);
    });
  },
});

export default repartosSlice.reducer;